import { useState, useMemo, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import { usePlatformName } from '../hooks/useWhitelabelOverrides';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Table, BarChart3, LineChart, PieChart, Hash, Filter, Check, X, Calendar, TrendingUp, Grid3X3, Info } from 'lucide-react';
import { Input } from './ui/input';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Combobox, type ComboboxOption } from './ui/combobox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { MultiSelectCombobox, type MultiSelectComboboxOption } from './ui/multi-select-combobox';
import { FilterTable } from './FilterTable';
import { DATE_TYPE_CALENDAR_FIELD_IDS } from '../services/analysis.service';
import type { Analysis, DateComparison } from '../services/analysis.service';
import type { ReportBuilderDimension, ReportBuilderMeasure } from '../services/reportBuilder.service';
import { getAvailableFormats, CUSTOM_FORMAT_PREFIX } from '../services/formatting.service';
import { useCustomDisplayFormats } from '../hooks/useCustomDisplayFormats';
import { useDimensions, useMeasures, useCalendarNode, fetchDimensionValues } from '../services/reportBuilder.service';
import { useReportingTree } from '../hooks/useReportingTree';
import { useAnalysis } from '../hooks/useAnalysis';
import { ReportBuilderState, SimpleGraph } from '../utils/reportBuilderState';
import { defaultComparatorOptions, metricComparatorOptions, type AnalysisDimensionFilter, type AnalysisMetricFilter } from '../utils/filterComparators';

export interface ChartEditorSidebarHandle {
  /** Programmatically trigger the same logic as clicking Apply */
  apply: () => void;
  /** Whether the sidebar has unapplied changes */
  hasChanges: boolean;
}

interface ChartEditorSidebarProps {
  analysis: Analysis;
  originalAnalysisId: string;
  dateRange?: string;
  customDateStart?: string;
  customDateEnd?: string;
  onDateRangeChange?: (dateRange: string, customStart?: string, customEnd?: string) => void;
  onApply: (updatedAnalysis: Analysis, isApplying?: boolean) => void;
  onClose?: () => void;
  // Optional: the analysis currently applied to the chart (for detecting pending changes)
  // If not provided, hasChanges compares against the `analysis` prop
  appliedAnalysis?: Analysis;
  // Notify parent when sidebar has/doesn't have pending changes
  onPendingChanges?: (hasPendingChanges: boolean) => void;
}

import { DateRangePicker } from './ui/date-range-picker';
import { useAvailableDashboardDateRangeOptions } from '../hooks/useAvailableDashboardDateRangeOptions';

const CHART_TYPES = [
  { value: 'TABLE', label: 'Table', icon: Table },
  { value: 'BAR', label: 'Bar', icon: BarChart3 },
  { value: 'LINE', label: 'Line', icon: LineChart },
  { value: 'PIE', label: 'Pie', icon: PieChart },
  { value: 'SINGLE_VALUE', label: 'Single Value', icon: Hash },
];

const SPECIAL_TYPES = [
  { value: 'PIVOT_TABLE', label: 'Pivot Table', icon: Grid3X3 },
  { value: 'PREMIUM_LTV_PIVOT_TABLE', label: 'Cohort Analysis (LTV)', icon: TrendingUp },
];

// Helper to check if a visualization type is a special type
const isSpecialType = (type: string) => SPECIAL_TYPES.some(t => t.value === type);

export const ChartEditorSidebar = forwardRef<ChartEditorSidebarHandle, ChartEditorSidebarProps>(function ChartEditorSidebar({
  analysis,
  originalAnalysisId,
  dateRange,
  customDateStart,
  customDateEnd,
  onDateRangeChange,
  onApply,
  onClose,
  appliedAnalysis,
  onPendingChanges,
}, ref) {
  const platformName = usePlatformName();
  const { data: dimensions } = useDimensions();
  const { data: measures } = useMeasures();
  const { data: reportingTree } = useReportingTree();
  const { data: calendarNode } = useCalendarNode();
  const { data: originalAnalysis } = useAnalysis(originalAnalysisId);
  const availableDateRangeOptions = useAvailableDashboardDateRangeOptions();

  // Local state for editing
  const [chartType, setChartType] = useState(analysis.visualization_type || 'TABLE');
  const [showSpecialTypes, setShowSpecialTypes] = useState(() => isSpecialType(analysis.visualization_type || ''));
  const [measureIds, setMeasureIds] = useState<string[]>(analysis.measure_ids || []);
  const [dimensionIds, setDimensionIds] = useState<string[]>(analysis.dimension_ids || []);
  const [dimensionIncludeAll, setDimensionIncludeAll] = useState<Record<string, boolean>>(
    analysis.dimension_include_all || {}
  );
  const [dimensionFilters, setDimensionFilters] = useState<AnalysisDimensionFilter[]>(
    analysis.dimension_filters || []
  );
  const [ignoreEmptyDimensions, setIgnoreEmptyDimensions] = useState(analysis.ignore_empty_dimensions || false);
  const [localDateRange, setLocalDateRange] = useState<string>(dateRange || 'all_time');
  const [localCustomDateStart, setLocalCustomDateStart] = useState<string>(customDateStart || '');
  const [localCustomDateEnd, setLocalCustomDateEnd] = useState<string>(customDateEnd || '');
  const [barMode, setBarMode] = useState<'grouped' | 'stacked' | 'overlay'>(
    (analysis.viz_options?.bar_mode as 'grouped' | 'stacked' | 'overlay') || 'grouped'
  );
  const [limit, setLimit] = useState<string>(analysis.limit ? String(analysis.limit) : '');
  const [sortDescending, setSortDescending] = useState<boolean>(() => {
    const dimSort = analysis.default_sort_order?.find(s => s.entity_type === 'DIMENSION');
    return dimSort ? !dimSort.ascending : true;
  });
  const [filtersDialogOpen, setFiltersDialogOpen] = useState(false);
  const [localDimensionFilters, setLocalDimensionFilters] = useState<AnalysisDimensionFilter[]>(
    analysis.dimension_filters || []
  );
  const [localMetricFilters, setLocalMetricFilters] = useState<AnalysisMetricFilter[]>(
    analysis.metric_filters || []
  );
  const [dateComparison, setDateComparison] = useState<DateComparison | null>(
    analysis.date_comparison || null
  );
  const [formatOverride, setFormatOverride] = useState<string>(
    (analysis.viz_options?.format_override as string) || ''
  );
  const [hideZeroes, setHideZeroes] = useState<boolean>(
    (analysis.viz_options?.hide_zeroes as boolean) || false
  );
  const [hideLegend, setHideLegend] = useState<boolean>(
    (analysis.viz_options?.hide_legend as boolean) || false
  );

  // Sync all state when analysis changes
  useEffect(() => {
    setChartType(analysis.visualization_type || 'TABLE');
    setShowSpecialTypes(isSpecialType(analysis.visualization_type || ''));
    setMeasureIds(analysis.measure_ids || []);
    setDimensionIds(analysis.dimension_ids || []);
    setDimensionIncludeAll(analysis.dimension_include_all || {});
    setDimensionFilters(analysis.dimension_filters || []);
    setBarMode((analysis.viz_options?.bar_mode as 'grouped' | 'stacked' | 'overlay') || 'grouped');
    setLimit(analysis.limit ? String(analysis.limit) : '');
    const dimSort = analysis.default_sort_order?.find(s => s.entity_type === 'DIMENSION');
    setSortDescending(dimSort ? !dimSort.ascending : true);
    setDateComparison(analysis.date_comparison || null);
    setFormatOverride((analysis.viz_options?.format_override as string) || '');
    setHideZeroes((analysis.viz_options?.hide_zeroes as boolean) || false);
    setHideLegend((analysis.viz_options?.hide_legend as boolean) || false);
  }, [analysis.id, analysis.visualization_type, analysis.measure_ids, analysis.dimension_ids, analysis.dimension_filters, analysis.viz_options, analysis.limit, analysis.default_sort_order, analysis.date_comparison, analysis.dimension_include_all]);

  // Sync date range when props change
  useEffect(() => {
    setLocalDateRange(dateRange || 'all_time');
    setLocalCustomDateStart(customDateStart || '');
    setLocalCustomDateEnd(customDateEnd || '');
  }, [dateRange, customDateStart, customDateEnd]);

  // Auto-set include_all for date dimensions when they're added by the user.
  // Only date dimensions support include_all; strip any non-date entries.
  // Only auto-enable for dimensions NOT already in the analysis prop — dimensions
  // that came from the analysis (e.g. after drilldown) should respect the
  // analysis's dimension_include_all as synced above.
  useEffect(() => {
    if (!calendarNode || !dimensions) return;
    const analysisDimIds = new Set(analysis.dimension_ids || []);
    setDimensionIncludeAll(prev => {
      const next: Record<string, boolean> = {};
      let changed = false;
      for (const dimId of dimensionIds) {
        const dim = dimensions.find(d => d.id === dimId);
        if (!dim || dim.pipeline_node_id !== calendarNode.id) continue;
        if (dimId in prev) {
          next[dimId] = prev[dimId];
        } else if (!analysisDimIds.has(dimId)) {
          next[dimId] = true;
          changed = true;
        }
      }
      if (!changed && Object.keys(next).length === Object.keys(prev).length) return prev;
      return next;
    });
  }, [dimensionIds, calendarNode, dimensions, analysis.dimension_ids]);

  // Build graph from reporting tree
  const graph = useMemo(() => {
    const g = new SimpleGraph();
    if (!reportingTree) return g;

    reportingTree.nodes.forEach(n => {
      g.setNode(n.id, n.title);
    });

    reportingTree.edges.forEach(e => {
      g.setEdge(e.from_id, e.to_id, e.relationship_id);
    });

    return g;
  }, [reportingTree]);

  // Initialize ReportBuilderState
  const reportBuilderState = useMemo(() => {
    if (graph && dimensions && measures) {
      return new ReportBuilderState(
        graph,
        dimensions,
        measures,
        dimensionIds,
        measureIds,
        chartType
      );
    }
    return null;
  }, [graph, dimensions, measures, dimensionIds, measureIds, chartType]);

  const availableMeasureIds = reportBuilderState?.availableMeasureIds || [];
  const availableDimensionIds = reportBuilderState?.availableDimensionIds || [];

  // Display-format options include tenant custom formats (selectable here;
  // they're created and managed from the metric editor).
  const { data: customDisplayFormats = [] } = useCustomDisplayFormats();
  const formatOptions = useMemo(
    () => [
      ...getAvailableFormats(),
      ...customDisplayFormats.map((f) => ({
        value: `${CUSTOM_FORMAT_PREFIX}${f.id}`,
        label: f.label,
      })),
    ],
    [customDisplayFormats]
  );

  // Measure options - filtered by availableMeasureIds
  const measureOptions = useMemo<ComboboxOption[]>(() => {
    if (!measures) return [];
    return measures
      .filter(measure => availableMeasureIds.includes(measure.id))
      .map(measure => ({
        value: measure.id,
        label: measure.name,
        disabled: false,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [measures, availableMeasureIds]);

  // Measure options for multi-select (table charts)
  const measureMultiOptions = useMemo<MultiSelectComboboxOption[]>(() => {
    if (!measures) return [];
    return measures
      .filter(measure => availableMeasureIds.includes(measure.id))
      .map(measure => ({
        value: measure.id,
        label: measure.name,
        disabled: false,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [measures, availableMeasureIds]);

  // IDs selected on this analysis that don't exist in the loaded dimensions list
  const missingDimensionIds = useMemo(() => {
    if (!dimensions) return new Set<string>();
    const knownIds = new Set(dimensions.map(d => d.id));
    return new Set(dimensionIds.filter(id => !knownIds.has(id)));
  }, [dimensions, dimensionIds]);

  const notFoundOption = (id: string): ComboboxOption => ({ value: id, label: '<NOT FOUND>', disabled: false });

  // Dimension options
  const dimensionOptions = useMemo<ComboboxOption[]>(() => {
    if (!dimensions) return [];
    const found = dimensions
      .filter(dimension => availableDimensionIds.includes(dimension.id))
      .map(dimension => ({
        value: dimension.id,
        label: dimension.name,
        disabled: false,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [...found, ...[...missingDimensionIds].map(notFoundOption)];
  }, [dimensions, availableDimensionIds, missingDimensionIds]);

  // Dimension options for filters (exclude DATE-type calendar dimensions — date range is controlled separately)
  // STRING-type calendar dimensions (Day of Week, Fiscal Month, etc.) are allowed through.
  const dimensionFilterOptions = useMemo<ComboboxOption[]>(() => {
    if (!dimensions) return [];
    const found = dimensions
      .filter(dimension =>
        availableDimensionIds.includes(dimension.id) &&
        !(dimension.pipeline_node_id === calendarNode?.id && DATE_TYPE_CALENDAR_FIELD_IDS.has(dimension.field_id))
      )
      .map(dimension => ({
        value: dimension.id,
        label: dimension.name,
        disabled: false,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [...found, ...[...missingDimensionIds].map(notFoundOption)];
  }, [dimensions, availableDimensionIds, calendarNode, missingDimensionIds]);

  // Dimension options for multi-select (table charts)
  const dimensionMultiOptions = useMemo<MultiSelectComboboxOption[]>(() => {
    if (!dimensions) return [];
    const found = dimensions
      .filter(dimension => availableDimensionIds.includes(dimension.id))
      .map(dimension => ({
        value: dimension.id,
        label: dimension.name,
        disabled: false,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [...found, ...[...missingDimensionIds].map(notFoundOption)];
  }, [dimensions, availableDimensionIds, missingDimensionIds]);

  // Dimension options for bar/line charts (first dimension)
  const dimensionOptions1 = useMemo<ComboboxOption[]>(() => {
    if (!dimensions) return [];
    const found = dimensions
      .filter(dimension => {
        if (chartType === 'LINE' && calendarNode && dimension.pipeline_node_id === calendarNode.id) {
          return true; // Calendar dimension is allowed for first dimension in line charts
        }
        return availableDimensionIds.includes(dimension.id);
      })
      .map(dimension => ({
        value: dimension.id,
        label: dimension.name,
        disabled: dimensionIds[1] !== dimension.id && dimensionIds.includes(dimension.id),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [...found, ...[...missingDimensionIds].map(notFoundOption)];
  }, [dimensions, calendarNode, availableDimensionIds, dimensionIds, chartType, missingDimensionIds]);

  // Dimension options for bar/line charts (second dimension)
  const dimensionOptions2 = useMemo<ComboboxOption[]>(() => {
    if (!dimensions) return [];
    const found = dimensions
      .filter(dimension => {
        if (chartType === 'LINE' && calendarNode && dimension.pipeline_node_id === calendarNode.id) {
          return false; // Exclude calendar dimensions from second dimension in line charts
        }
        return availableDimensionIds.includes(dimension.id);
      })
      .map(dimension => ({
        value: dimension.id,
        label: dimension.name,
        disabled: dimensionIds[0] !== dimension.id && dimensionIds.includes(dimension.id),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [...found, ...[...missingDimensionIds].map(notFoundOption)];
  }, [dimensions, availableDimensionIds, dimensionIds, chartType, calendarNode, missingDimensionIds]);

  // Date dimension options (for LTV Pivot Table - Acquisition Period)
  const dateDimensionOptions = useMemo<ComboboxOption[]>(() => {
    if (!dimensions || !calendarNode) return [];
    return dimensions
      .filter(dimension => dimension.pipeline_node_id === calendarNode.id)
      .map(dimension => ({
        value: dimension.id,
        label: dimension.name,
        disabled: false,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [dimensions, calendarNode]);

  // All dimension options (not filtered by availability - for special chart types)
  const allDimensionOptions = useMemo<ComboboxOption[]>(() => {
    if (!dimensions) return [];
    return dimensions
      .map(dimension => ({
        value: dimension.id,
        label: dimension.name,
        disabled: false,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [dimensions]);

  // All measure options (not filtered by availability - for special chart types)
  const allMeasureOptions = useMemo<ComboboxOption[]>(() => {
    if (!measures) return [];
    return measures
      .map(measure => ({
        value: measure.id,
        label: measure.name,
        disabled: false,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [measures]);

  // Check if there are changes
  // Compare against appliedAnalysis if provided (to detect pending changes not yet applied to chart)
  // Otherwise compare against analysis prop
  const hasChanges = useMemo(() => {
    const compareAnalysis = appliedAnalysis || analysis;
    const dateRangeChanged = localDateRange !== (dateRange || 'all_time') ||
      localCustomDateStart !== (customDateStart || '') ||
      localCustomDateEnd !== (customDateEnd || '');

    const barModeChanged = chartType === 'BAR' && barMode !== ((compareAnalysis.viz_options?.bar_mode as string) || 'grouped');

    const hideZeroesChanged = chartType === 'LINE' && hideZeroes !== ((compareAnalysis.viz_options?.hide_zeroes as boolean) || false);

    const hideLegendChanged = chartType === 'PIE' && hideLegend !== ((compareAnalysis.viz_options?.hide_legend as boolean) || false);

    const formatOverrideChanged = chartType === 'SINGLE_VALUE' && formatOverride !== ((compareAnalysis.viz_options?.format_override as string) || '');

    const parsedLimit = limit ? parseInt(limit, 10) : undefined;
    const limitChanged = chartType === 'TABLE' && (parsedLimit || undefined) !== (compareAnalysis.limit || undefined);

    return (
      chartType !== compareAnalysis.visualization_type ||
      JSON.stringify(measureIds) !== JSON.stringify(compareAnalysis.measure_ids) ||
      JSON.stringify(dimensionIds) !== JSON.stringify(compareAnalysis.dimension_ids) ||
      JSON.stringify(dimensionFilters) !== JSON.stringify(compareAnalysis.dimension_filters || []) ||
      JSON.stringify(localMetricFilters) !== JSON.stringify(compareAnalysis.metric_filters || []) ||
      dateRangeChanged ||
      barModeChanged ||
      hideZeroesChanged ||
      hideLegendChanged ||
      formatOverrideChanged ||
      limitChanged ||
      ignoreEmptyDimensions !== (compareAnalysis.ignore_empty_dimensions || false) ||
      (dateComparison || null) !== (compareAnalysis.date_comparison || null) ||
      JSON.stringify(dimensionIncludeAll) !== JSON.stringify(compareAnalysis.dimension_include_all || {})
    );
  }, [chartType, measureIds, dimensionIds, dimensionFilters, localMetricFilters, analysis, appliedAnalysis, localDateRange, localCustomDateStart, localCustomDateEnd, dateRange, customDateStart, customDateEnd, barMode, hideZeroes, hideLegend, formatOverride, limit, ignoreEmptyDimensions, dateComparison, dimensionIncludeAll]);

  // Handle chart type change
  const handleChartTypeChange = useCallback((newType: string) => {
    const previousType = chartType;
    setChartType(newType);
    
    // Reset metrics/dimensions based on chart type constraints
    if (newType === 'BAR' || newType === 'LINE') {
      // Limit to 1 measure for LINE only
      if (newType === 'LINE') {
        if (measureIds.length > 1) {
          setMeasureIds([measureIds[0]]);
        }
      }
      // Reset bar mode to grouped when switching to BAR
      if (newType === 'BAR' && previousType !== 'BAR') {
        setBarMode('grouped');
      }
      
      // If switching from TABLE to BAR/LINE in drilldown, use the drilled-down dimension
      if (previousType === 'TABLE' && originalAnalysis) {
        const originalDimensionIds = originalAnalysis.dimension_ids || [];
        const originalFilterDimensionIds = (originalAnalysis.dimension_filters || []).map(f => f.dimension_id);
        
        // Find the dimension that was drilled down on
        // For metric drilldown: it's the last dimension added (not in original)
        // For dimension drilldown: it's the dimension with a new filter (not in original filters)
        let drilledDownDimensionId: string | null = null;
        
        // Check for dimensions added in metric drilldown
        const newDimensions = dimensionIds.filter(id => !originalDimensionIds.includes(id));
        if (newDimensions.length > 0) {
          drilledDownDimensionId = newDimensions[newDimensions.length - 1];
        } else {
          // Check for dimensions with new filters (dimension drilldown)
          const newFilterDimensions = (dimensionFilters || [])
            .map(f => f.dimension_id)
            .filter(id => id && !originalFilterDimensionIds.includes(id));
          if (newFilterDimensions.length > 0) {
            drilledDownDimensionId = newFilterDimensions[0];
          } else {
            // Fallback: use the first dimension that has a filter with a single value
            const singleValueFilter = (dimensionFilters || []).find(f => {
              if (!f.dimension_id || !f.value) return false;
              const values = Array.isArray(f.value) ? f.value : [f.value];
              return values.length === 1 && f.comparator === 'IN';
            });
            if (singleValueFilter) {
              drilledDownDimensionId = singleValueFilter.dimension_id;
            }
          }
        }
        
        // If we found a drilled-down dimension, use only that
        if (drilledDownDimensionId) {
          setDimensionIds([drilledDownDimensionId]);
        } else {
          // Otherwise, clear all dimensions
          setDimensionIds([]);
        }
      } else {
        // Limit to 2 dimensions for other cases
        if (dimensionIds.length > 2) {
          setDimensionIds(dimensionIds.slice(0, 2));
        }
      }
    } else if (newType === 'PIE') {
      // Limit to exactly 1 measure and 1 dimension
      if (measureIds.length > 1) {
        setMeasureIds([measureIds[0]]);
      }
      if (dimensionIds.length > 1) {
        setDimensionIds([dimensionIds[0]]);
      }
      if (dimensionIds.length === 0 && measureIds.length > 0) {
        // If no dimensions but we have a measure, we need to prompt user to select a dimension
        // But don't auto-clear, let them select
      }
    } else if (newType === 'SINGLE_VALUE') {
      // Limit to 1 measure, dimensions are optional
      if (measureIds.length > 1) {
        setMeasureIds([measureIds[0]]);
      }
      // Don't clear dimensions - they are optional for SINGLE_VALUE charts
    } else if (newType === 'PIVOT_TABLE') {
      if (measureIds.length > 1) {
        setMeasureIds([measureIds[0]]);
      }
    } else if (newType === 'PREMIUM_LTV_PIVOT_TABLE') {
      // LTV Pivot Table needs exactly 2 dimensions and 1-2 measures
      // Reset to empty arrays so user can configure from scratch
      if (!isSpecialType(previousType)) {
        setMeasureIds([]);
        setDimensionIds([]);
      }
    }
  }, [chartType, measureIds, dimensionIds, originalAnalysis, dimensionFilters]);

  // Handle bar mode change with cleanup
  const handleBarModeChange = useCallback((newMode: 'grouped' | 'stacked' | 'overlay') => {
    setBarMode(newMode);
    if (newMode === 'grouped' || newMode === 'stacked') {
      // Keep metrics and up to 2 dimensions
      if (dimensionIds.length > 2) {
        setDimensionIds(dimensionIds.slice(0, 2));
      }
    } else if (newMode === 'overlay') {
      // Keep metrics, keep only first dimension (clear second)
      if (dimensionIds.length > 1) {
        setDimensionIds([dimensionIds[0]]);
      }
    }
  }, [measureIds, dimensionIds]);

  // Handle measure changes for bar/line charts
  const handleSetMeasure = useCallback((measureId: string) => {
    if (measureId) {
      setMeasureIds([measureId]);
    } else {
      setMeasureIds([]);
    }
  }, []);

  // Handle dimension changes for bar/line charts
  const handleSetDimension0 = useCallback((dimensionId: string) => {
    const newIds: string[] = [];
    if (dimensionId) {
      newIds[0] = dimensionId;
      if (dimensionIds[1] && dimensionIds[1] !== dimensionId) {
        newIds[1] = dimensionIds[1];
      }
    } else {
      if (dimensionIds[1]) {
        newIds[0] = dimensionIds[1];
      }
    }
    setDimensionIds(newIds.filter(id => id));
  }, [dimensionIds]);

  const handleSetDimension1 = useCallback((dimensionId: string) => {
    const newIds: string[] = [];
    if (dimensionIds[0]) {
      newIds[0] = dimensionIds[0];
    }
    if (dimensionId) {
      newIds[1] = dimensionId;
    }
    setDimensionIds(newIds.filter(id => id));
  }, [dimensionIds]);

  // Handle apply
  const handleApply = useCallback(() => {
    // Filter out invalid sort configs: remove any that reference dimensions/metrics that don't exist
    let filteredSortOrder = analysis.default_sort_order;
    if (filteredSortOrder && filteredSortOrder.length > 0) {
      filteredSortOrder = filteredSortOrder.filter(sort => {
        // Skip if no entity_id
        if (!sort.entity_id) {
          return false;
        }
        // Skip if entity_id doesn't exist in the updated analysis
        if (sort.entity_type === 'METRIC') {
          return measureIds.includes(sort.entity_id);
        } else if (sort.entity_type === 'DIMENSION') {
          return dimensionIds.includes(sort.entity_id);
        }
        return false;
      });
      // Set to undefined if empty array
      if (filteredSortOrder.length === 0) {
        filteredSortOrder = undefined;
      }
    }

    // For SINGLE_VALUE charts with a dimension, set sort order from the toggle
    if (chartType === 'SINGLE_VALUE' && dimensionIds.length > 0) {
      filteredSortOrder = [{
        entity_type: 'DIMENSION',
        entity_id: dimensionIds[0],
        ascending: !sortDescending,
      }];
    }
    
    const parsedLimit = limit ? parseInt(limit, 10) : undefined;
    const updatedAnalysis: Analysis = {
      ...analysis,
      visualization_type: chartType,
      measure_ids: measureIds,
      dimension_ids: dimensionIds,
      dimension_filters: dimensionFilters,
      metric_filters: localMetricFilters,
      default_sort_order: filteredSortOrder,
      viz_options: {
        ...analysis.viz_options,
        ...(chartType === 'BAR' ? { bar_mode: barMode } : {}),
        ...(chartType === 'LINE' ? { hide_zeroes: hideZeroes } : {}),
        ...(chartType === 'PIE' ? { hide_legend: hideLegend } : {}),
        ...(chartType === 'SINGLE_VALUE' && formatOverride ? { format_override: formatOverride } : { format_override: undefined }),
      },
      limit: chartType === 'TABLE' && parsedLimit ? parsedLimit : undefined,
      ignore_empty_dimensions: ignoreEmptyDimensions,
      date_comparison: dateComparison,
      dimension_include_all: dimensionIncludeAll,
    };
    onApply(updatedAnalysis, true); // Pass true to indicate we're applying changes
    
    // Apply date range changes if they exist
    if (onDateRangeChange) {
      const dateRangeChanged = localDateRange !== (dateRange || 'all_time') ||
        localCustomDateStart !== (customDateStart || '') ||
        localCustomDateEnd !== (customDateEnd || '');
      
      if (dateRangeChanged) {
        if (localDateRange === 'custom') {
          onDateRangeChange(localDateRange, localCustomDateStart, localCustomDateEnd);
        } else {
          onDateRangeChange(localDateRange);
        }
      }
    }
  }, [analysis, chartType, barMode, hideZeroes, hideLegend, formatOverride, limit, measureIds, dimensionIds, dimensionFilters, localMetricFilters, sortDescending, onApply, onDateRangeChange, localDateRange, localCustomDateStart, localCustomDateEnd, dateRange, customDateStart, customDateEnd, ignoreEmptyDimensions, dateComparison, dimensionIncludeAll]);

  // Expose apply + hasChanges to parent via ref
  useImperativeHandle(ref, () => ({
    apply: handleApply,
    hasChanges,
  }), [handleApply, hasChanges]);

  // Notify parent of pending changes
  useEffect(() => {
    onPendingChanges?.(hasChanges);
  }, [hasChanges, onPendingChanges]);

  // Get selected measure/dimension names for display
  const selectedMeasures = useMemo(() => {
    if (!measures) return [];
    return measureIds.map(id => measures.find(m => m.id === id)).filter(Boolean) as ReportBuilderMeasure[];
  }, [measures, measureIds]);

  const selectedDimensions = useMemo(() => {
    if (!dimensions) return [];
    return dimensionIds.map(id => dimensions.find(d => d.id === id)).filter(Boolean) as ReportBuilderDimension[];
  }, [dimensions, dimensionIds]);

  // Measure options for metric filters - only measures currently selected in the chart
  const selectedMeasureFilterOptions = useMemo<ComboboxOption[]>(() => {
    if (!measures) return [];
    return measureIds
      .map(id => measures.find(m => m.id === id))
      .filter(Boolean)
      .map(measure => ({
        value: measure!.id,
        label: measure!.name,
        disabled: false,
      }));
  }, [measures, measureIds]);

  const activeDimensionFilterCount = dimensionFilters.filter(f => f.dimension_id && f.comparator).length;
  const activeMetricFilterCount = localMetricFilters.filter(f => f.metric_id && f.comparator).length;
  const activeFilterCount = activeDimensionFilterCount + activeMetricFilterCount;

  // Sync local filters when analysis changes
  useEffect(() => {
    setLocalDimensionFilters(analysis.dimension_filters || []);
  }, [analysis.dimension_filters]);

  useEffect(() => {
    setLocalMetricFilters(analysis.metric_filters || []);
  }, [analysis.metric_filters]);

  return (
    <div className="h-full w-full flex flex-col border-r border-border bg-background overflow-hidden">
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        
        {/* Chart type buttons */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-muted-foreground">Chart Type</label>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{showSpecialTypes ? 'Special' : 'Basic'}</span>
              <Switch
                checked={showSpecialTypes}
                onCheckedChange={setShowSpecialTypes}
                className="scale-75"
              />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            {showSpecialTypes ? (
              // Special chart types
              SPECIAL_TYPES.map((type) => {
                const Icon = type.icon;
                const isSelected = chartType === type.value;
                return (
                  <Button
                    key={type.value}
                    variant={isSelected ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleChartTypeChange(type.value)}
                    className="gap-2 justify-start"
                  >
                    <Icon className="h-4 w-4" />
                    {type.label}
                  </Button>
                );
              })
            ) : (
              // Basic chart types
              CHART_TYPES.map((type) => {
                const Icon = type.icon;
                const isSelected = chartType === type.value;
                return (
                  <Button
                    key={type.value}
                    variant={isSelected ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleChartTypeChange(type.value)}
                    className="gap-2 justify-start"
                  >
                    <Icon className="h-4 w-4" />
                    {type.label}
                  </Button>
                );
              })
            )}
          </div>
        </div>

        {/* Bar Mode Selector */}
        {chartType === 'BAR' && (
          <div className="space-y-2">
            <div className="flex items-center gap-1">
              <label className="text-sm font-medium text-muted-foreground">Bar Mode</label>
              <Popover>
                <PopoverTrigger asChild>
                  <button className="text-muted-foreground hover:text-foreground">
                    <Info className="h-3.5 w-3.5" />
                  </button>
                </PopoverTrigger>
                <PopoverContent side="right" align="start" className="w-72 text-sm space-y-3">
                  <p className="font-medium">Bar Chart Modes</p>
                  <div className="space-y-2 text-muted-foreground">
                    <p><span className="font-medium text-foreground">Side by Side</span> — Multiple metrics or a group dimension with bars placed next to each other. Best for comparing categories.</p>
                    <p><span className="font-medium text-foreground">Stacked</span> — Multiple metrics or a stack dimension stacked on top of each other. Shows part-to-whole relationships.</p>
                    <p><span className="font-medium text-foreground">Overlay</span> — Multiple metrics rendered at the same position, overlapping. Useful for comparing totals vs. actuals.</p>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <div className="flex gap-1">
              {([
                { value: 'grouped', label: 'Side by Side' },
                { value: 'stacked', label: 'Stacked' },
                { value: 'overlay', label: 'Overlay' },
              ] as const).map((mode) => (
                <Button
                  key={mode.value}
                  variant={barMode === mode.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleBarModeChange(mode.value)}
                  className="flex-1 text-xs"
                >
                  {mode.label}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Hide Zeroes Toggle (Line charts only) */}
        {chartType === 'LINE' && (
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-muted-foreground">Hide Zeroes</label>
            <Switch
              checked={hideZeroes}
              onCheckedChange={setHideZeroes}
              className="scale-75"
            />
          </div>
        )}

        {/* Hide Legend Toggle (Pie charts only) */}
        {chartType === 'PIE' && (
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-muted-foreground">Hide Legend</label>
            <Switch
              checked={hideLegend}
              onCheckedChange={setHideLegend}
              className="scale-75"
            />
          </div>
        )}

        {/* Date Range Selector */}
        {onDateRangeChange && (
          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">Date Range</label>
            <DateRangePicker
              value={localDateRange}
              customDateStart={localCustomDateStart}
              customDateEnd={localCustomDateEnd}
              availableOptions={availableDateRangeOptions}
              onChange={(value, customStart, customEnd) => {
                setLocalDateRange(value);
                if (value === 'custom') {
                  setLocalCustomDateStart(customStart || '');
                  setLocalCustomDateEnd(customEnd || '');
                } else {
                  setLocalCustomDateStart('');
                  setLocalCustomDateEnd('');
                }
              }}
            />
          </div>
        )}

        {/* Date Comparison */}
        {chartType === 'SINGLE_VALUE' && localDateRange && localDateRange !== 'all_time' && (
          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">Compare To</label>
            <Combobox
              options={[
                { value: 'none', label: 'No comparison' },
                { value: 'PREVIOUS_PERIOD', label: 'Previous period' },
                { value: 'SAME_PERIOD_LAST_YEAR', label: 'Same period last year' },
              ]}
              value={dateComparison || 'none'}
              onValueChange={(val) => setDateComparison(val === 'none' ? null : val as DateComparison)}
            />
          </div>
        )}

        {/* Format Override for Single Value */}
        {chartType === 'SINGLE_VALUE' && (() => {
          const selectedMeasure = measures?.find(m => measureIds.includes(m.id));
          const defaultFmtLabel = selectedMeasure?.formatter
            ? formatOptions.find(f => f.value === selectedMeasure.formatter)?.label
            : undefined;
          return (
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">Format Override</label>
              <Combobox
                options={[
                  { value: 'none', label: defaultFmtLabel ? `Default (${defaultFmtLabel})` : 'No format' },
                  ...formatOptions,
                ]}
                value={formatOverride || 'none'}
                onValueChange={(val) => setFormatOverride(val === 'none' ? '' : val)}
              />
            </div>
          );
        })()}

        {/* Filters Button */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-muted-foreground">Filters</label>
          <Button
            variant="outline"
            size="sm"
            className={`w-full justify-start ${activeFilterCount > 0 ? "border-purple-500 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-950" : ""}`}
            onClick={() => setFiltersDialogOpen(true)}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
            {activeFilterCount > 0 && (
              <span className="ml-auto px-1.5 py-0.5 text-xs font-semibold rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">
                {activeFilterCount}
              </span>
            )}
          </Button>
        </div>

        {/* Filters Dialog */}
        <Dialog 
          open={filtersDialogOpen} 
          onOpenChange={(open) => {
            if (!open) {
              // Closing without clicking "Update Filters" — discard changes
              setLocalDimensionFilters(dimensionFilters);
              setLocalMetricFilters(analysis.metric_filters || []);
            }
            setFiltersDialogOpen(open);
          }}
        >
          <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
            <DialogHeader>
              <DialogTitle>Filters</DialogTitle>
              <DialogDescription>
                Add filters to narrow down the data shown in your chart.
              </DialogDescription>
            </DialogHeader>
            <DialogBody className="flex-1 overflow-y-auto py-4">
              <Tabs defaultValue="dimensions">
                <TabsList className="w-full">
                  <TabsTrigger value="dimensions" className="flex-1">
                    Dimension Filters
                    {activeDimensionFilterCount > 0 && (
                      <span className="ml-1.5 px-1.5 py-0.5 text-xs font-semibold rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">
                        {activeDimensionFilterCount}
                      </span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="metrics" className="flex-1">
                    Metric Filters
                    {activeMetricFilterCount > 0 && (
                      <span className="ml-1.5 px-1.5 py-0.5 text-xs font-semibold rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">
                        {activeMetricFilterCount}
                      </span>
                    )}
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="dimensions">
                  <FilterTable
                    filters={localDimensionFilters.map(f => ({
                      columnId: f.dimension_id,
                      comparator: f.comparator,
                      value: f.value,
                    }))}
                    onChange={(newFilters) => {
                      setLocalDimensionFilters(newFilters.map(f => ({
                        dimension_id: f.columnId,
                        comparator: f.comparator,
                        value: f.value,
                      })));
                    }}
                    columnOptions={dimensionFilterOptions}
                    columnLabel="Dimension"
                    showComparator={true}
                    comparatorOptions={defaultComparatorOptions}
                    defaultComparator="IN"
                    defaultValue={[]}
                    valueMode="multiselect"
                    onFetchValueOptions={fetchDimensionValues}
                  />
                </TabsContent>
                <TabsContent value="metrics">
                  <FilterTable
                    filters={localMetricFilters.map(f => ({
                      columnId: f.metric_id,
                      comparator: f.comparator,
                      value: f.value != null ? String(f.value) : '',
                    }))}
                    onChange={(newFilters) => {
                      setLocalMetricFilters(newFilters.map(f => ({
                        metric_id: f.columnId,
                        comparator: f.comparator,
                        value: f.value !== '' ? String(f.value) : undefined,
                      })));
                    }}
                    columnOptions={selectedMeasureFilterOptions}
                    columnLabel="Metric"
                    showComparator={true}
                    comparatorOptions={metricComparatorOptions}
                    defaultComparator="GTE"
                    defaultValue=""
                    valueMode="text"
                  />
                </TabsContent>
              </Tabs>
            </DialogBody>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  // Discard changes and close
                  setLocalDimensionFilters(dimensionFilters);
                  setLocalMetricFilters(analysis.metric_filters || []);
                  setFiltersDialogOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  // Save to local sidebar state (not applied to chart yet — user must hit Apply)
                  setDimensionFilters(localDimensionFilters);
                  setFiltersDialogOpen(false);
                }}
              >
                Done
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Pivot Table Custom Form */}
        {chartType === 'PIVOT_TABLE' && (
          <>
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">Metric</label>
              <Combobox
                options={measureOptions}
                value={measureIds[0] || ''}
                onValueChange={(value) => {
                  setMeasureIds(value ? [value] : []);
                }}
                placeholder="Select metric..."
                emptyText="No metrics available"
              />
              <p className="text-xs text-muted-foreground">
                The value to aggregate in each pivot cell.
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">Column Dimension</label>
              <Combobox
                options={dimensionOptions.filter(d => !dimensionIds.slice(1).includes(d.value))}
                value={dimensionIds[0] || ''}
                onValueChange={(value) => {
                  const newIds = [...dimensionIds];
                  newIds[0] = value;
                  setDimensionIds(newIds.filter(Boolean));
                }}
                placeholder={measureIds.length === 0 ? "Select a metric first" : "Select column dimension..."}
                emptyText="No dimensions available"
                disabled={measureIds.length === 0}
              />
              <p className="text-xs text-muted-foreground">
                Unique values of this dimension become column headers.
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">Row Dimensions</label>
              <MultiSelectCombobox
                options={dimensionMultiOptions.filter(d => d.value !== dimensionIds[0])}
                value={dimensionIds.slice(1)}
                onValueChange={(values) => {
                  setDimensionIds([dimensionIds[0], ...values].filter(Boolean));
                }}
                placeholder={measureIds.length === 0 ? "Select a metric first" : "Select row dimensions..."}
                emptyText="No dimensions available"
                disabled={measureIds.length === 0}
              />
              <p className="text-xs text-muted-foreground">
                These dimensions form the row labels on the left side of the pivot table.
              </p>
            </div>
          </>
        )}

        {/* LTV Pivot Table Custom Form */}
        {chartType === 'PREMIUM_LTV_PIVOT_TABLE' && (
          <>
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">Total Sales Metric</label>
              <Combobox
                options={allMeasureOptions}
                value={measureIds[0] || ''}
                onValueChange={(value) => {
                  const newMeasureIds = [...measureIds];
                  newMeasureIds[0] = value;
                  setMeasureIds(newMeasureIds.filter(Boolean));
                }}
                placeholder="Select metric..."
                emptyText="No metrics available"
              />
              <p className="text-xs text-muted-foreground">
                Select the metric that represents total sales revenue. This will be used to calculate LTV.
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">Ad Spend Metric (Optional)</label>
              <Combobox
                options={allMeasureOptions}
                value={measureIds[1] || ''}
                onValueChange={(value) => {
                  const newMeasureIds = [measureIds[0] || ''];
                  if (value) {
                    newMeasureIds[1] = value;
                  }
                  setMeasureIds(newMeasureIds.filter(Boolean));
                }}
                placeholder="Optional..."
                emptyText="No metrics available"
              />
              <p className="text-xs text-muted-foreground">
                Select the metric that represents ad spend to calculate CAC payback period.
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">Acquisition Period</label>
              <Combobox
                options={dateDimensionOptions}
                value={dimensionIds[0] || ''}
                onValueChange={(value) => {
                  const newDimensionIds = [...dimensionIds];
                  newDimensionIds[0] = value;
                  setDimensionIds(newDimensionIds.filter(Boolean));
                }}
                placeholder="Select date dimension..."
                emptyText="No date dimensions available"
              />
              <p className="text-xs text-muted-foreground">
                Select the date dimension to group customers by acquisition cohort.
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">Customer Dimension</label>
              <Combobox
                options={allDimensionOptions}
                value={dimensionIds[1] || ''}
                onValueChange={(value) => {
                  const newDimensionIds = [dimensionIds[0] || ''];
                  if (value) {
                    newDimensionIds[1] = value;
                  }
                  setDimensionIds(newDimensionIds.filter(Boolean));
                }}
                placeholder="Select dimension..."
                emptyText="No dimensions available"
              />
              <p className="text-xs text-muted-foreground">
                Select the dimension representing each customer to calculate repurchase rates.
              </p>
            </div>
          </>
        )}

        {/* Standard Metrics */}
        {!isSpecialType(chartType) && (
          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">
              {chartType === 'BAR' ? 'Metrics' :
               (chartType === 'LINE' || chartType === 'PIE' || chartType === 'SINGLE_VALUE') ? 'Metric' : 'Metrics'}
            </label>
            {chartType === 'BAR' ? (
              // All BAR modes allow multiple metrics
              // Exception: if a second dimension is selected (stacked/grouped), keep single metric
              (dimensionIds.length > 1) ? (
                <Combobox
                  options={measureOptions}
                  value={measureIds[0] || ''}
                  onValueChange={handleSetMeasure}
                  placeholder="Select metric..."
                  emptyText="No metrics available"
                />
              ) : (
                <MultiSelectCombobox
                  options={measureMultiOptions}
                  value={measureIds}
                  onValueChange={setMeasureIds}
                  placeholder="Select metrics..."
                  emptyText="No metrics available"
                  dragToReorder
                />
              )
            ) : (chartType === 'LINE' || chartType === 'PIE' || chartType === 'SINGLE_VALUE') ? (
              <Combobox
                options={measureOptions}
                value={measureIds[0] || ''}
                onValueChange={handleSetMeasure}
                placeholder="Select metric..."
                emptyText="No metrics available"
              />
            ) : (
              <MultiSelectCombobox
                options={measureMultiOptions}
                value={measureIds}
                onValueChange={setMeasureIds}
                placeholder="Select metrics..."
                emptyText="No metrics available"
                dragToReorder={chartType === 'TABLE'}
              />
            )}
            {chartType === 'BAR' && (barMode === 'stacked' || barMode === 'grouped') && (
              <p className="text-xs text-muted-foreground">
                {dimensionIds.length > 1
                  ? `Using ${barMode === 'stacked' ? 'stack' : 'group'} dimension — single metric only.`
                  : `Add multiple metrics, or use a single metric with a ${barMode === 'stacked' ? 'stack' : 'group'} dimension below.`}
              </p>
            )}
          </div>
        )}

        {/* Standard Dimensions */}
        {!isSpecialType(chartType) && (
          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">
              Dimensions{chartType === 'SINGLE_VALUE' ? (measureIds.length > 0 && measures?.some(m => measureIds.includes(m.id) && m.metric_type === 'COHORT' && m.aggregator === 'COUNT_REACTIVATED_CONDITION') ? ' (Required)' : ' (Optional)') : ''}
            </label>
            {chartType === 'SINGLE_VALUE' && measureIds.length > 0 && measures?.some(m => measureIds.includes(m.id) && m.metric_type === 'COHORT' && m.aggregator === 'COUNT_REACTIVATED_CONDITION') && (
              <p className="text-xs text-amber-600">
                Reactivated metrics require a date dimension to define the inactivity gap.
              </p>
            )}
            {(chartType === 'BAR' || chartType === 'LINE') ? (
              <div className="space-y-2">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">X-Axis</label>
                  <Combobox
                    options={dimensionOptions1}
                    value={dimensionIds[0] || ''}
                    onValueChange={handleSetDimension0}
                    placeholder={measureIds.length === 0 ? "Select a metric first" : "Select dimension..."}
                    emptyText="No dimensions available"
                    disabled={measureIds.length === 0}
                  />
                </div>
                {/* BAR grouped/stacked: show second dimension only if single metric */}
                {/* BAR overlay: no second dimension */}
                {/* LINE: show group dimension */}
                {chartType === 'BAR' && (barMode === 'stacked' || barMode === 'grouped') ? (
                  measureIds.length <= 1 && (
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">
                        {barMode === 'stacked' ? 'Stack' : 'Group'} Dimension (Optional)
                      </label>
                      <Combobox
                        options={dimensionOptions2}
                        value={dimensionIds[1] || ''}
                        onValueChange={handleSetDimension1}
                        placeholder={measureIds.length === 0 ? "Select a metric first" : "Optional..."}
                        emptyText="No dimensions available"
                        disabled={measureIds.length === 0}
                      />
                    </div>
                  )
                ) : chartType === 'LINE' ? (
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Group (Optional)</label>
                    <Combobox
                      options={dimensionOptions2}
                      value={dimensionIds[1] || ''}
                      onValueChange={handleSetDimension1}
                      placeholder={measureIds.length === 0 ? "Select a metric first" : "Optional..."}
                      emptyText="No dimensions available"
                      disabled={measureIds.length === 0}
                    />
                  </div>
                ) : null}
              </div>
            ) : chartType === 'PIE' ? (
              <Combobox
                options={dimensionOptions1}
                value={dimensionIds[0] || ''}
                onValueChange={handleSetDimension0}
                placeholder={measureIds.length === 0 ? "Select a metric first" : "Select dimension..."}
                emptyText="No dimensions available"
                disabled={measureIds.length === 0}
              />
            ) : (
              <MultiSelectCombobox
                options={dimensionMultiOptions}
                value={dimensionIds}
                onValueChange={setDimensionIds}
                placeholder={measureIds.length === 0 ? "Select a metric first" : "Select dimensions..."}
                emptyText="No dimensions available"
                dragToReorder={chartType === 'TABLE'}
                disabled={measureIds.length === 0}
              />
            )}

            {/* Include all dates toggle — only for date dimensions */}
            {dimensionIds.length > 0 && calendarNode && dimensionIds.some(dimId => {
              const dim = dimensions?.find(d => d.id === dimId);
              return dim && dim.pipeline_node_id === calendarNode.id;
            }) && (
              <div className="space-y-1 pt-1">
                {dimensionIds.map(dimId => {
                  const dim = dimensions?.find(d => d.id === dimId);
                  if (!dim || dim.pipeline_node_id !== calendarNode.id) return null;
                  return (
                    <div key={dimId}>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground truncate mr-2">
                          Include all dates
                        </span>
                        <Switch
                          checked={!!dimensionIncludeAll[dimId]}
                          onCheckedChange={(checked) => {
                            setDimensionIncludeAll(prev => {
                              const next = { ...prev };
                              next[dimId] = checked;
                              return next;
                            });
                          }}
                          className="scale-75"
                        />
                      </div>
                      <p className="text-[10px] text-muted-foreground/70 mt-0.5">
                        Show dates with no data as zero-filled rows
                      </p>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Sort order toggle for SINGLE_VALUE with dimensions */}
            {chartType === 'SINGLE_VALUE' && dimensionIds.length > 0 && (
              <div className="flex items-center justify-between pt-1">
                <label className="text-sm text-muted-foreground">Sort Descending</label>
                <Switch
                  checked={sortDescending}
                  onCheckedChange={setSortDescending}
                  className="scale-75"
                />
              </div>
            )}
          </div>
        )}

        {/* Row Limit (TABLE only) */}
        {chartType === 'TABLE' && (
          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">Row Limit</label>
            <Input
              type="number"
              min={1}
              placeholder="No limit"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Maximum number of rows to display. Leave empty for default.
            </p>
          </div>
        )}


        {/* Filtering explanation */}
        {!isSpecialType(chartType) && (
          <Popover>
            <PopoverTrigger asChild>
              <button className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 cursor-pointer">
                Why aren't some of my metrics or dimensions available?
              </button>
            </PopoverTrigger>
            <PopoverContent side="right" align="start" className="w-80 text-sm space-y-2">
              <p className="font-medium">Why are some options missing?</p>
              <p className="text-muted-foreground">
                Not all metrics and dimensions can be used together. {platformName} only shows you the combinations that will produce valid results.
              </p>
              <p className="text-muted-foreground">
                As you select metrics, the dimension list narrows to show only the ones that work with your chosen metrics — and vice versa.
              </p>
              <p className="text-muted-foreground">
                If something you expect is missing, it may need to be connected in the semantic layer first. Reach out to your data team for help.
              </p>
            </PopoverContent>
          </Popover>
        )}
      </div>

      {/* Apply Button - Fixed at bottom */}
      <div className="p-4 border-t border-border flex-shrink-0">
        <Button
          variant={hasChanges ? 'default' : 'outline'}
          size="sm"
          onClick={handleApply}
          disabled={!hasChanges}
          className={`w-full ${hasChanges ? 'bg-primary text-primary-foreground' : ''}`}
        >
          <Check className="h-4 w-4 mr-2" />
          Apply
        </Button>
      </div>
    </div>
  );
});

